import { configureTenantBridge } from '@/api/client'
import type { ApiError } from '@/api/errors'

/**
 * Organizzazione selezionata — TEN-06.
 *
 * Store esterno a React, come `auth/session-store.ts`, e per lo stesso identico
 * motivo: il valore serve a `api/client.ts` per comporre l'header
 * `X-Organization-Id`, e quel file non è un componente — non può leggere uno
 * stato con un hook. Il ponte in fondo al file lo registra nel client, così
 * nessuno dei due deve importare l'altro.
 *
 * ## Perché `localStorage` e non `sessionStorage`
 *
 * La sessione muore con la tab di proposito (il refresh token è un segreto), ma
 * "sto lavorando sulla palestra di Bologna" non è un segreto: è una preferenza,
 * e reimpostarla a ogni apertura del pannello sarebbe il modo più rapido di far
 * fare a qualcuno una modifica nell'organizzazione sbagliata. L'id di
 * un'organizzazione da solo non autorizza nulla — il server ricontrolla
 * l'appartenenza a **ogni** richiesta (TEN-04) — quindi persisterlo non aggiunge
 * superficie di attacco.
 *
 * ## Cosa succede quando la selezione non vale più
 *
 * Un'appartenenza può finire mentre il pannello è aperto (o fra due sessioni):
 * da lì in poi ogni richiesta con quell'header risponde `403`. Il client chiama
 * `rejectOrganization()`, la selezione torna a "predefinita" e il pannello
 * riparte dall'organizzazione che il server sceglie da sé, invece di restare
 * bloccato su un errore che l'operatore non può interpretare.
 */

const STORAGE_KEY = 'gymmi-admin.organization'

interface Snapshot {
  /** `null` = nessuna scelta esplicita: decide il server. */
  organizationId: string | null
  /** Cambia a ogni selezione: è la chiave con cui la cache si accorge del cambio. */
  revision: number
}

function readStored(): string | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw && raw.trim() !== '' ? raw : null
  } catch {
    // Storage negato (modalità privata restrittiva): la selezione vale per questa
    // pagina e non sopravvive al ricaricamento. Accettabile, non è un errore.
    return null
  }
}

function writeStored(organizationId: string | null): void {
  try {
    if (organizationId) localStorage.setItem(STORAGE_KEY, organizationId)
    else localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Vedi sopra.
  }
}

let snapshot: Snapshot = { organizationId: readStored(), revision: 0 }

const listeners = new Set<() => void>()

export function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function getSnapshot(): Snapshot {
  return snapshot
}

export function getSelectedOrganizationId(): string | null {
  return snapshot.organizationId
}

function emit(organizationId: string | null): void {
  snapshot = { organizationId, revision: snapshot.revision + 1 }
  writeStored(organizationId)
  for (const listener of listeners) listener()
}

/**
 * Cambio di organizzazione.
 *
 * Selezionare l'organizzazione già attiva è un no-op **esplicito**: senza,
 * ogni click sulla voce già scelta svuoterebbe la cache e farebbe ripartire da
 * capo tutte le query a schermo.
 */
export function selectOrganization(organizationId: string | null): void {
  if (snapshot.organizationId === organizationId) return
  emit(organizationId)
  onChangedHook?.({ kind: 'selected', organizationId })
}

/** Chiamata dal client quando il server rifiuta l'organizzazione selezionata. */
export function rejectOrganization(error: ApiError): void {
  if (snapshot.organizationId === null) return
  emit(null)
  onChangedHook?.({ kind: 'rejected', error })
}

/**
 * Azzera la selezione senza notificare: si usa alla fine della sessione, dove la
 * cache viene già svuotata da chi chiude la sessione e un secondo svuotamento
 * sarebbe solo rumore.
 */
export function clearOrganization(): void {
  if (snapshot.organizationId === null) return
  emit(null)
}

/** Perché l'organizzazione attiva è cambiata. */
export type OrganizationChange =
  { kind: 'selected'; organizationId: string | null } | { kind: 'rejected'; error: ApiError }

let onChangedHook: ((change: OrganizationChange) => void) | null = null

/**
 * Registra la reazione al cambio di organizzazione.
 *
 * È **obbligatorio** che chi la registra svuoti la cache di TanStack Query: le
 * chiavi non contengono l'id dell'organizzazione, quindi senza svuotamento il
 * pannello mostrerebbe gli elenchi della palestra precedente sotto il nome di
 * quella nuova — un errore che non ha alcun segnale visivo.
 */
export function setOnOrganizationChanged(handler: (change: OrganizationChange) => void): void {
  onChangedHook = handler
}

configureTenantBridge({
  getOrganizationId: getSelectedOrganizationId,
  onOrganizationRejected: rejectOrganization,
})
